import { NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { first, Subject, takeUntil } from 'rxjs';

import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsDividerComponent } from '@design-system/ds-divider/component/ds-divider.component';
import { DsIconButtonComponent } from '@design-system/ds-icon-button/component/ds-icon-button.component';
import { DsInputComponent } from '@design-system/ds-input/component/ds-input.component';
import { DsRadioGroupComponent } from '@design-system/ds-radio-group/component/ds-radio-group.component';
import { DsSelectComponent } from '@design-system/ds-select/component/ds-select.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { NotificationService } from '@notification/service';
import { CustomerService } from '@services/customer.service';
import { GoogleMapsLoaderService } from '@services/google-maps-loader.service';
import { formatPhoneNumber } from '@utils/phone-number.utl';
import { AddressType, DefaultGUID, GroundResponseTimeOptions, RegionNames, RegionOptions } from 'src/app/@shared/constants/Constant';
import { IDropdownDataOptions } from 'src/app/model/common.model';
import { CustomerList, CustomerPayload, CustomerType } from 'src/app/service/customer.model';
import { MemberService } from 'src/app/service/member.service';
import { ICityViewModel, StateViewModel } from 'src/app/service/state.model';
import { VendorService } from 'src/app/service/vendor.service';
import {
  CustomerEditModalDataInterface,
  CustomerEditModalDetailInterface,
  CustomerEditModalResult,
} from '../interfaces/customer-edit-modal-data.interface';
import { PoMappingInterface } from '@modals/po-mapping-modal/interfaces/po-mapping-modal-data.interface';
import { PoMappingModalService } from '@modals/po-mapping-modal/services/po-mapping-modal.service';
import {
  ADDRESS_VISIBILITY_OPTIONS,
  CONTRACTED_CUSTOMER_OPTIONS,
  CUSTOMER_EMAIL_MAX_LENGTH,
  CUSTOMER_NAME_MAX_LENGTH,
  CUSTOMER_PHONE_MAX_LENGTH,
  CUSTOMER_PHONE_PATTERN,
  CUSTOMER_STREET_ADDRESS_MAX_LENGTH,
  CUSTOMER_ZIPCODE_MAX_LENGTH,
  CUSTOMER_ZIPCODE_MIN_LENGTH,
  CUSTOMER_ZIPCODE_PATTERN,
  EDIT_CUSTOMER_HEADER,
} from '../properties/customer-edit-modal.properties';
import { CustomerEditFormGroup } from '../types/customer-edit-form-group.type';

/**
 * Add/Edit Customer modal.
 *
 * Address rules: the address block is optional as a whole, but once an Address Type is picked the
 * address is considered in use, so Street Address, Zipcode, State and City become required.
 */
@Component({
  selector: 'app-customer-edit-modal',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    ReactiveFormsModule,
    DsTextComponent,
    DsInputComponent,
    DsSelectComponent,
    DsButtonComponent,
    DsDividerComponent,
    DsIconButtonComponent,
    DsRadioGroupComponent,
  ],
  templateUrl: './customer-edit-modal.component.html',
  styleUrls: ['./customer-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerEditModalComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly vendorService = inject(VendorService);

  private readonly customerService = inject(CustomerService);

  private readonly googleMapsLoader = inject(GoogleMapsLoaderService);

  private readonly memberService = inject(MemberService);

  private readonly notificationService = inject(NotificationService);

  private readonly poMappingModalService = inject(PoMappingModalService);

  private readonly dialogRef = inject<MatDialogRef<CustomerEditModalComponent, CustomerEditModalResult>>(MatDialogRef);

  private readonly dialogData = inject<CustomerEditModalDataInterface>(MAT_DIALOG_DATA);

  private readonly destroy$ = new Subject<void>();

  readonly modalTitle: string = this.dialogData.header;

  readonly isEditMode: boolean = this.dialogData.header === EDIT_CUSTOMER_HEADER;

  readonly addressTypes: string[] = AddressType;

  readonly regionOptions: IDropdownDataOptions[] = RegionOptions;

  readonly groundResponseTimeOptions: IDropdownDataOptions[] = GroundResponseTimeOptions;

  readonly contractedOptions = CONTRACTED_CUSTOMER_OPTIONS;

  readonly addressVisibilityOptions = ADDRESS_VISIBILITY_OPTIONS;

  readonly nameMaxLength = CUSTOMER_NAME_MAX_LENGTH;

  readonly phoneMaxLength = CUSTOMER_PHONE_MAX_LENGTH;

  readonly emailMaxLength = CUSTOMER_EMAIL_MAX_LENGTH;

  readonly streetAddressMaxLength = CUSTOMER_STREET_ADDRESS_MAX_LENGTH;

  readonly zipcodeMaxLength = CUSTOMER_ZIPCODE_MAX_LENGTH;

  customerTypes: CustomerType[] = [];

  stateList: StateViewModel[] = [];

  cityList: ICityViewModel[] = [];

  poBoxValue = '';

  invalidEmail = false;

  isSaving = false;

  readonly form: CustomerEditFormGroup = this.formBuilder.group({
    name: this.formBuilder.control<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(CUSTOMER_NAME_MAX_LENGTH)],
    }),
    customerType: this.formBuilder.control<string>('', { nonNullable: true, validators: [Validators.required] }),
    phone: this.formBuilder.control<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(CUSTOMER_PHONE_MAX_LENGTH), Validators.pattern(CUSTOMER_PHONE_PATTERN)],
    }),
    emailId: this.formBuilder.control<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(CUSTOMER_EMAIL_MAX_LENGTH)],
    }),
    region: this.formBuilder.control<string>('', { nonNullable: true }),
    isContracted: this.formBuilder.control<boolean>(false, { nonNullable: true, validators: [Validators.required] }),
    groundResponseTime: this.formBuilder.control<number>(90, { nonNullable: true }),
    businessEmail: this.formBuilder.control<string>('', { nonNullable: true }),
    address: this.formBuilder.group({
      addressLocationType: this.formBuilder.control<string | null>(null),
      isGlobal: this.formBuilder.control<boolean>(true, { nonNullable: true, validators: [Validators.required] }),
      streetAddress: this.formBuilder.control<string>('', {
        nonNullable: true,
        validators: [Validators.maxLength(CUSTOMER_STREET_ADDRESS_MAX_LENGTH)],
      }),
      zipcode: this.formBuilder.control<string>('', {
        nonNullable: true,
        validators: [
          Validators.minLength(CUSTOMER_ZIPCODE_MIN_LENGTH),
          Validators.maxLength(CUSTOMER_ZIPCODE_MAX_LENGTH),
          Validators.pattern(CUSTOMER_ZIPCODE_PATTERN),
        ],
      }),
      stateId: this.formBuilder.control<string>('', { nonNullable: true }),
      city: this.formBuilder.control<string>('', { nonNullable: true }),
    }),
  }) as CustomerEditFormGroup;

  /** Customer payload sent to the API — the form only overwrites the fields it owns. */
  private customer: CustomerPayload = {} as CustomerPayload;

  /** PO# mapping keyed by organ type, edited in a separate modal. */
  private poBox: PoMappingInterface | null = null;

  /** Name of the selected state, used to build the geocoding query. */
  private selectedState = '';

  private readonly customerId: string = this.dialogData.customerDetail?.customerId ?? '';

  get isAddressTypeSelected(): boolean {
    return !!this.form.controls.address.controls.addressLocationType.value;
  }

  ngOnInit(): void {
    this.getStateList();
    this.getCustomerType();
    this.assignValue();
    this.subscribeOnAddressTypeChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Loads the cities of the selected state, dropping the city picked for the previous one.
   */
  onStateChange(): void {
    this.form.controls.address.controls.city.setValue('');
    this.cityList = [];

    this.getCityByState(this.form.controls.address.controls.stateId.value);
  }

  /**
   * Formats the phone number once the user leaves the field, keeping the caret untouched while typing.
   */
  onPhoneBlur(): void {
    const phoneControl = this.form.controls.phone;

    phoneControl.setValue(formatPhoneNumber(phoneControl.value));
  }

  /**
   * Validates the comma separated business emails.
   */
  onBusinessEmailChange(): void {
    const businessEmail: string = this.form.controls.businessEmail.value;

    if (businessEmail.length === 0) {
      this.invalidEmail = false;

      return;
    }

    const emails: string[] = businessEmail.split(',');

    this.invalidEmail = emails.some((email: string): boolean => !this.validateEmail(email));
  }

  openPoMappingModal(): void {
    this.poMappingModalService
      .openDialog$({ poBox: this.poBox })
      .pipe(first())
      .subscribe((poBox: PoMappingInterface | null): void => {
        if (!poBox) {
          return;
        }

        this.poBox = poBox;
        this.poBoxValue = this.setPOValue(poBox);
        this.changeDetectorRef.markForCheck();
      });
  }

  onSave(): void {
    if (this.isSaving) {
      return;
    }

    if (this.form.invalid || this.invalidEmail) {
      this.form.markAllAsTouched();
      this.changeDetectorRef.markForCheck();

      return;
    }

    this.applyFormToCustomer();

    if (this.isEditMode) {
      this.updateCustomerDetail();

      return;
    }

    this.addCustomer();
  }

  /**
   * Maps the customer coming from the caller into the form and the API payload.
   */
  private assignValue(): void {
    const customerDetail: CustomerEditModalDetailInterface | undefined = this.dialogData.customerDetail;

    if (!this.isEditMode || !customerDetail) {
      this.resetCustomerData();
      this.poBoxValue = '';

      return;
    }

    this.getCityByState(customerDetail.address?.stateId);

    const businessEmail: string = this.normalizeBusinessEmail(customerDetail.contact?.businessEmail);

    this.customer = {
      name: customerDetail.name,
      opo: customerDetail.opo,
      customerType: customerDetail.customerTypeName,
      region: customerDetail.region,
      poBox: customerDetail.poBox,
      contact: {
        emailId: customerDetail.contact?.emailId,
        businessEmail,
        phone: formatPhoneNumber(customerDetail.contact?.phone),
        primaryContact: customerDetail.contact?.primaryContact,
        emergencyContact: customerDetail.contact?.emergencyContact,
      },
      address: {
        addressId: customerDetail.address?.addressId,
        streetAddress: customerDetail.address?.streetAddress,
        city: customerDetail.address?.city,
        stateId: customerDetail.address?.stateId,
        zipcode: customerDetail.address?.zipcode,
        latitude: '',
        longitude: '',
        addressLocationType: customerDetail.address?.addressLocationType,
        isGlobal: customerDetail.address?.isGlobal,
      },
      isContracted: customerDetail.isContracted ?? false,
      groundResponseTime: customerDetail.groundResponseTime,
    } as CustomerPayload;

    this.poBox = (customerDetail.poBox as PoMappingInterface) ?? null;

    if (this.poBox) {
      this.poBoxValue = this.setPOValue(this.poBox);
    }

    this.patchForm();
  }

  /**
   * Default payload used for the add mode.
   */
  private resetCustomerData(): void {
    this.customer = {
      customerId: DefaultGUID,
      name: '',
      partnerType: '',
      customerType: '',
      poBox: {},
      region: '',
      contact: {
        emailId: '',
        businessEmail: '',
        phone: '',
        primaryContact: '',
        emergencyContact: '',
      },
      address: {
        addressId: DefaultGUID,
        streetAddress: '',
        city: '',
        stateId: '',
        zipcode: '',
        addressLocationType: null,
        isGlobal: true,
      },
      files: [],
      isContracted: false,
      groundResponseTime: 90,
    } as unknown as CustomerPayload;

    this.poBox = {};
  }

  private patchForm(): void {
    this.form.patchValue({
      name: this.customer.name ?? '',
      customerType: this.customer.customerType ?? '',
      phone: this.customer.contact?.phone ?? '',
      emailId: this.customer.contact?.emailId ?? '',
      region: this.customer.region ?? '',
      isContracted: this.customer.isContracted ?? false,
      groundResponseTime: this.customer.groundResponseTime,
      businessEmail: this.customer.contact?.businessEmail ?? '',
      address: {
        addressLocationType: this.customer.address?.addressLocationType ?? null,
        isGlobal: this.customer.address?.isGlobal ?? true,
        streetAddress: this.customer.address?.streetAddress ?? '',
        zipcode: this.customer.address?.zipcode ?? '',
        stateId: this.customer.address?.stateId ?? '',
        city: this.customer.address?.city ?? '',
      },
    });
  }

  /**
   * Copies the form values into the payload, leaving every field the form does not own untouched.
   */
  private applyFormToCustomer(): void {
    const formValue = this.form.getRawValue();

    this.customer.name = formValue.name;
    this.customer.customerType = formValue.customerType;
    this.customer.region = formValue.region;
    this.customer.isContracted = formValue.isContracted;
    this.customer.groundResponseTime = formValue.groundResponseTime;
    this.customer.poBox = this.poBox;
    this.customer.contact = {
      ...this.customer.contact,
      phone: formValue.phone,
      emailId: formValue.emailId,
      businessEmail: formValue.businessEmail,
    };
    this.customer.address = {
      ...this.customer.address,
      addressLocationType: formValue.address.addressLocationType,
      isGlobal: formValue.address.isGlobal,
      streetAddress: formValue.address.streetAddress,
      zipcode: formValue.address.zipcode,
      // The API expects a nullable Guid, so an unselected state has to go out as null, not ''.
      stateId: formValue.address.stateId || null,
      city: formValue.address.city,
    };

    this.normalizePhoneNumbers();
  }

  private updateCustomerDetail(): void {
    this.isSaving = true;

    this.customerService
      .updateCustomerDetail(this.customer, this.customerId)
      .pipe(first())
      .subscribe({
        next: (): void => {
          this.invalidEmail = false;
          this.notificationService.success('Record Updated Successfully', 'Record Updated');

          if (this.memberService.customerList$.value.length > 0) {
            const index: number = this.memberService.customerList$.value.findIndex(
              (customer: CustomerList): boolean => customer.customerID == this.customerId,
            );

            if (index !== -1) {
              this.memberService.customerList$.value[index].region = this.customer.region;
            }
          }

          this.dialogRef.close('refresh');
        },
        error: (): void => {
          this.isSaving = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private addCustomer(): void {
    if (this.customer.region == RegionNames.LifeSource) {
      this.notificationService.warning('This region is no longer available. Please select another region.', 'Invalid Region');

      return;
    }

    this.isSaving = true;

    // Address is optional, so an empty address must not be geocoded - it would always fail
    // and block the customer from being created.
    if (!this.hasAddressData()) {
      this.createCustomer();

      return;
    }

    // The Maps API is loaded on demand, so the Geocoder may not exist yet when the modal is saved.
    this.googleMapsLoader
      .load()
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe((isLoaded: boolean): void => {
        if (!isLoaded) {
          this.isSaving = false;
          this.notificationService.error('Unable to verify the address right now. Please try again.', 'Error');
          this.changeDetectorRef.markForCheck();

          return;
        }

        this.geocodeAddressAndCreateCustomer();
      });
  }

  private geocodeAddressAndCreateCustomer(): void {
    const countryName = 'USA';
    const address: string = `${this.customer.address.streetAddress}, ${this.customer.address.city}, ${this.selectedState} ${countryName} ${this.customer.address.zipcode}`;
    const geocoder: google.maps.Geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address }, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus): void => {
      if (status == 'OK') {
        const longitude: number = results[0].geometry.location.lng();
        const latitude: number = results[0].geometry.location.lat();

        if (longitude && latitude) {
          this.customer.address.latitude = latitude.toString();
          this.customer.address.longitude = longitude.toString();
        }

        this.createCustomer();

        return;
      }

      this.isSaving = false;
      this.notificationService.error('Location not found! Please check your address.', 'Error');
      this.changeDetectorRef.markForCheck();
    });
  }

  private createCustomer(): void {
    this.customerService
      .addCustomer(this.customer)
      .pipe(first())
      .subscribe({
        next: (): void => {
          this.notificationService.success('Customer Added Successfully', 'Record Saved');
          this.isSaving = false;
          this.dialogRef.close('refresh');
        },
        error: (): void => {
          this.isSaving = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private getStateList(): void {
    this.vendorService
      .getStateList()
      .pipe(first())
      .subscribe((response: StateViewModel[]): void => {
        this.stateList = response;
        this.resolveSelectedState();
        this.changeDetectorRef.markForCheck();
      });
  }

  private getCustomerType(): void {
    this.vendorService
      .getCustomerType()
      .pipe(first())
      .subscribe((response: CustomerType[]): void => {
        this.customerTypes = response;
        this.changeDetectorRef.markForCheck();
      });
  }

  private getCityByState(stateId: string | undefined): void {
    if (stateId == undefined || stateId == '') {
      return;
    }

    this.resolveSelectedState();

    this.vendorService
      .getCityByState(stateId)
      .pipe(first())
      .subscribe((response: ICityViewModel[]): void => {
        this.cityList = response;
        this.changeDetectorRef.markForCheck();
      });
  }

  private resolveSelectedState(): void {
    const stateId: string = this.form.controls.address.controls.stateId.value || (this.customer.address?.stateId ?? '');
    const state: StateViewModel | undefined = this.stateList.find((item: StateViewModel): boolean => item.stateId == stateId);

    this.selectedState = state ? state.stateName : '';
  }

  /**
   * Picking an Address Type makes the rest of the address required.
   */
  private subscribeOnAddressTypeChange(): void {
    this.form.controls.address.controls.addressLocationType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((): void => this.updateAddressValidators());

    this.updateAddressValidators();
  }

  private updateAddressValidators(): void {
    const addressControls = this.form.controls.address.controls;
    const requiredValidator: ValidatorFn[] = this.isAddressTypeSelected ? [Validators.required] : [];

    addressControls.streetAddress.setValidators([Validators.maxLength(CUSTOMER_STREET_ADDRESS_MAX_LENGTH), ...requiredValidator]);
    addressControls.zipcode.setValidators([
      Validators.minLength(CUSTOMER_ZIPCODE_MIN_LENGTH),
      Validators.maxLength(CUSTOMER_ZIPCODE_MAX_LENGTH),
      Validators.pattern(CUSTOMER_ZIPCODE_PATTERN),
      ...requiredValidator,
    ]);
    addressControls.stateId.setValidators(requiredValidator);
    addressControls.city.setValidators(requiredValidator);

    addressControls.streetAddress.updateValueAndValidity({ emitEvent: false });
    addressControls.zipcode.updateValueAndValidity({ emitEvent: false });
    addressControls.stateId.updateValueAndValidity({ emitEvent: false });
    addressControls.city.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Builds the readonly PO# display value out of the saved PO# mapping.
   */
  private setPOValue(poBox: PoMappingInterface): string {
    let poValue = '';

    Object.keys(poBox).forEach((key: string): void => {
      if (poBox[key] != '') {
        poValue += poBox[key] + ', ';
      }
    });

    return poValue.slice(0, poValue.length - 2);
  }

  private normalizeBusinessEmail(businessEmail: string | undefined | null): string {
    if (businessEmail == null) {
      return '';
    }

    return String(businessEmail)
      .split(',')
      .filter((email: string): boolean => email != '')
      .map((email: string): string => email.trim())
      .toString();
  }

  private normalizePhoneNumbers(): void {
    this.customer.contact.phone = this.customer.contact.phone.replace(/[^0-9\s]/g, '').replace(/\s/, '');

    if (this.customer.contact.emergencyContact != null && this.customer.contact.emergencyContact != '') {
      this.customer.contact.emergencyContact = this.customer.contact.emergencyContact.replace(/[^0-9\s]/g, '').replace(/\s/, '');
    }

    if (this.customer.contact.primaryContact != null && this.customer.contact.primaryContact != '') {
      this.customer.contact.primaryContact = this.customer.contact.primaryContact.replace(/[^0-9\s]/g, '').replace(/\s/, '');
    }
  }

  private hasAddressData(): boolean {
    const { streetAddress, city, stateId, zipcode } = this.customer.address;

    return !!(streetAddress?.trim() || city?.trim() || stateId || zipcode?.trim());
  }

  private validateEmail(mail: string): boolean {
    mail = mail.trim();

    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(mail) && mail.includes('.') && mail.includes('@');
  }
}
